import { createFileRoute } from "@tanstack/react-router";

import { ScrollScrub } from "@/components/scroll-scrub/scroll-scrub";
import { RouteLink, SealButton, WhatsAppLink } from "@/components/site/ctas";
import {
  Consolidation,
  DivisionsAccordion,
  Engines,
  Layers,
  Markets,
  Partners,
  ProcessRail,
  ProofStrip,
  References,
} from "@/components/site/home-sections";
import { Reveal } from "@/components/site/motion";
import { CONTACT_EMAIL, getDict, href, type Lang } from "@/content/site";
import { buildScenes, scrollScrubScenesByLang, scrollScrubTheme } from "../../scroll-scrub-scenes";

export const Route = createFileRoute("/$lang/")({
  loader: ({ params }) => ({ lang: params.lang as Lang }),
  head: ({ loaderData }) => {
    const t = getDict(loaderData?.lang ?? "en");
    const ld = [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Insidermark",
        alternateName: "Insidermark | Global Sourcing Partner",
        description: t.journey[0].body,
        email: CONTACT_EMAIL,
        address: { "@type": "PostalAddress", addressLocality: "Konya", addressCountry: "TR" },
        areaServed: "Africa",
        knowsAbout: t.divisions.map((d) => d.name),
      },
      { "@context": "https://schema.org", "@type": "WebSite", name: "Insidermark", inLanguage: loaderData?.lang ?? "en" },
    ];
    return { scripts: [{ type: "application/ld+json", children: JSON.stringify(ld) }] };
  },
  component: LangHomePage,
});

function LangHomePage() {
  const { lang } = Route.useLoaderData();
  return <HomePage lang={lang} />;
}

function scenesFor(lang: Lang) {
  const t = getDict(lang);
  const cached = scrollScrubScenesByLang[lang];
  if (cached) return cached;
  const scenes = buildScenes(t);
  // Hero chapter carries the two hero CTAs; the engine renders them inside
  // the chapter copy so they scrub with the film.
  scenes[0] = {
    ...scenes[0],
    actions: (
      <>
        <SealButton to={href(lang, "/rfq")}>{t.nav.rfq}</SealButton>
        <RouteLink to={href(lang, "/how-we-work")}>{t.hero.secondary}</RouteLink>
      </>
    ),
  };
  scenes[4] = {
    ...scenes[4],
    actions: <RouteLink to={href(lang, "/markets")}>{t.nav.markets}</RouteLink>,
  };
  scrollScrubScenesByLang[lang] = scenes;
  return scenes;
}

export function HomePage({ lang = "tr" }: { lang?: Lang }) {
  const t = getDict(lang);
  const scenes = scenesFor(lang);
  return (
    <main>
      <ScrollScrub scenes={scenes} theme={scrollScrubTheme} chaptersLabel={t.a11y.chapters} />
      <div data-surface="light">
        <ProofStrip t={t} />
        <Partners lang={lang} t={t} />
        <DivisionsAccordion lang={lang} t={t} />
        <Layers t={t} />
        <Engines lang={lang} t={t} />
      </div>
      <Consolidation t={t} />
      <Markets lang={lang} t={t} />
      <ProcessRail lang={lang} t={t} />
      <References t={t} />
      <section className="im-section">
        <div className="im-plate im-plate--steel" aria-hidden="true" />
        <div className="im-wrap im-rel">
          <Reveal className="im-closing">
            <h2 className="im-display max-w-[16ch]">{t.closing.title}</h2>
            <p className="im-body-text mx-auto">{t.closing.body}</p>
            <div className="im-closing__ctas">
              <SealButton to={href(lang, "/rfq")}>{t.nav.rfq}</SealButton>
              <WhatsAppLink label={t.nav.whatsapp} text={t.hero.waText} />
              <RouteLink to={href(lang, "/contact")}>{t.contact.desk.title}</RouteLink>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
